import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Building2, 
  LogOut, 
  Menu, 
  X, 
  Home,
  Globe,
  ChevronRight,
  Clock,
  Mail,
  Phone,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Moon River Logo Component - Using official PNG logo HD (Sidebar version)
const MoonRiverLogoSidebar = () => (
  <img 
    src="/logo-full-transparent.png" 
    alt="Moon River Real Estate" 
    className="h-12 w-auto object-contain"
  />
);

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, apptRes] = await Promise.all([
          axios.get(`${API}/api/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/api/appointments`, { withCredentials: true }),
        ]);
        setStats(statsRes.data);
        setAppointments(apptRes.data);

        // Fetch leads only for admin
        if (user?.role === 'admin') {
          const leadsRes = await axios.get(`${API}/api/leads`, { withCredentials: true });
          setLeads(leadsRes.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    toast.success(language === 'fr' ? 'Déconnexion réussie' : 'Logged out successfully');
    navigate('/');
  };

  const isAdmin = user?.role === 'admin';

  const sidebarLinks = [
    { icon: LayoutDashboard, label: t('dashboard.overview'), href: '/dashboard', active: true },
    { icon: Calendar, label: t('dashboard.myAppointments'), href: '/dashboard/appointments' },
    ...(isAdmin ? [
      { icon: Users, label: 'Leads', href: '/dashboard/leads' },
      { icon: Building2, label: language === 'fr' ? 'Biens' : 'Properties', href: '/dashboard/properties' },
    ] : []),
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-surface border-r border-border-subtle transform transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="dashboard-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border-subtle">
            <Link to="/" className="flex items-center">
              <MoonRiverLogoSidebar />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {sidebarLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                  link.active
                    ? 'bg-primary-gold/10 text-primary-gold'
                    : 'text-muted-text hover:bg-surface-elevated hover:text-foreground'
                }`}
              >
                <link.icon size={20} strokeWidth={1.5} />
                <span>{link.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-border-subtle">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-gold/20 flex items-center justify-center">
                <span className="text-primary-gold font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-text truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={toggleLanguage}
                variant="outline"
                size="sm"
                className="flex-1 btn-secondary text-xs"
              >
                <Globe size={14} />
                <span className="uppercase ml-1">{language}</span>
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex-1 btn-secondary text-xs"
                data-testid="dashboard-logout"
              >
                <LogOut size={14} />
                <span className="ml-1">{t('nav.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 glass px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 text-foreground"
              >
                {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div>
                <h1 className="font-serif text-2xl text-foreground">
                  {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
                </h1>
                <p className="text-sm text-muted-text">
                  {isAdmin 
                    ? (language === 'fr' ? 'Administration' : 'Administration')
                    : (language === 'fr' ? 'Espace Client' : 'Client Area')}
                </p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 text-muted-text hover:text-foreground transition-colors"
            >
              <Home size={18} />
              <span className="hidden sm:inline">{t('nav.home')}</span>
            </Link>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-6 lg:p-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {isAdmin ? (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded bg-primary-gold/10 flex items-center justify-center">
                      <Users size={20} className="text-primary-gold" />
                    </div>
                    <span className="text-muted-text text-sm">{t('dashboard.stats.totalLeads')}</span>
                  </div>
                  <p className="font-serif text-3xl text-foreground">{stats?.total_leads || 0}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded bg-success/10 flex items-center justify-center">
                      <TrendingUp size={20} className="text-success" />
                    </div>
                    <span className="text-muted-text text-sm">{t('dashboard.stats.newLeads')}</span>
                  </div>
                  <p className="font-serif text-3xl text-foreground">{stats?.new_leads || 0}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded bg-primary-gold/10 flex items-center justify-center">
                      <Calendar size={20} className="text-primary-gold" />
                    </div>
                    <span className="text-muted-text text-sm">{t('dashboard.stats.appointments')}</span>
                  </div>
                  <p className="font-serif text-3xl text-foreground">{stats?.total_appointments || 0}</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="card-elevated p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded bg-primary-gold/10 flex items-center justify-center">
                      <Building2 size={20} className="text-primary-gold" />
                    </div>
                    <span className="text-muted-text text-sm">{t('dashboard.stats.properties')}</span>
                  </div>
                  <p className="font-serif text-3xl text-foreground">{stats?.total_properties || 0}</p>
                </motion.div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-elevated p-6 col-span-2"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded bg-primary-gold/10 flex items-center justify-center">
                    <Calendar size={20} className="text-primary-gold" />
                  </div>
                  <span className="text-muted-text text-sm">{t('dashboard.myAppointments')}</span>
                </div>
                <p className="font-serif text-3xl text-foreground">{stats?.my_appointments || 0}</p>
              </motion.div>
            )}
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Appointments */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card-elevated p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-foreground">
                  {isAdmin 
                    ? (language === 'fr' ? 'Rendez-vous à venir' : 'Upcoming Appointments')
                    : t('dashboard.myAppointments')}
                </h2>
                <Link to="/dashboard/appointments" className="text-primary-gold text-sm flex items-center gap-1 hover:text-primary-gold-hover">
                  {language === 'fr' ? 'Voir tout' : 'View all'}
                  <ChevronRight size={16} />
                </Link>
              </div>
              {appointments.length === 0 ? (
                <p className="text-muted-text text-center py-8">
                  {language === 'fr' ? 'Aucun rendez-vous' : 'No appointments'}
                </p>
              ) : (
                <div className="space-y-4">
                  {appointments.slice(0, 5).map((appt) => (
                    <div
                      key={appt.appointment_id}
                      className="flex items-center gap-4 p-4 bg-surface rounded border border-border-subtle"
                    >
                      <div className="w-12 h-12 rounded bg-primary-gold/10 flex flex-col items-center justify-center">
                        <span className="text-xs text-primary-gold font-medium">
                          {new Date(appt.date).toLocaleDateString('fr-FR', { day: '2-digit' })}
                        </span>
                        <span className="text-xs text-muted-text">
                          {new Date(appt.date).toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground font-medium truncate">{appt.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-text">
                          <Clock size={14} />
                          <span>{appt.time_slot}</span>
                          <span className="text-primary-gold">• {appt.subject}</span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${
                        appt.status === 'confirmed' 
                          ? 'bg-success/20 text-success' 
                          : 'bg-primary-gold/20 text-primary-gold'
                      }`}>
                        {appt.status === 'confirmed' 
                          ? (language === 'fr' ? 'Confirmé' : 'Confirmed')
                          : appt.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Leads (Admin only) */}
            {isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="card-elevated p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl text-foreground">
                    {language === 'fr' ? 'Derniers Leads' : 'Recent Leads'}
                  </h2>
                  <Link to="/dashboard/leads" className="text-primary-gold text-sm flex items-center gap-1 hover:text-primary-gold-hover">
                    {language === 'fr' ? 'Voir tout' : 'View all'}
                    <ChevronRight size={16} />
                  </Link>
                </div>
                {leads.length === 0 ? (
                  <p className="text-muted-text text-center py-8">
                    {language === 'fr' ? 'Aucun lead' : 'No leads'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {leads.slice(0, 5).map((lead) => (
                      <div
                        key={lead.lead_id}
                        className="p-4 bg-surface rounded border border-border-subtle"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-foreground font-medium">{lead.name}</p>
                          <span className={`px-2 py-1 text-xs rounded ${
                            lead.status === 'new'
                              ? 'bg-primary-gold/20 text-primary-gold'
                              : 'bg-muted/20 text-muted-text'
                          }`}>
                            {lead.status === 'new' 
                              ? (language === 'fr' ? 'Nouveau' : 'New')
                              : lead.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-muted-text">
                          <span className="flex items-center gap-1">
                            <Mail size={14} />
                            {lead.email}
                          </span>
                          {lead.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {lead.phone}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-2 text-xs">
                          <span className="px-2 py-1 bg-surface-elevated rounded text-muted-text">
                            {lead.budget}
                          </span>
                          <span className="px-2 py-1 bg-surface-elevated rounded text-muted-text">
                            {lead.investment_type}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Properties Card for Client */}
            {!isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="card-elevated p-6"
              >
                <h2 className="font-serif text-xl text-foreground mb-6">
                  {language === 'fr' ? 'Accès Portfolio' : 'Portfolio Access'}
                </h2>
                <p className="text-muted-text mb-6">
                  {language === 'fr' 
                    ? 'En tant que client authentifié, vous avez accès à notre portfolio Off-Market exclusif.'
                    : 'As an authenticated client, you have access to our exclusive Off-Market portfolio.'}
                </p>
                <Link to="/#portfolio">
                  <Button className="btn-primary w-full">
                    {language === 'fr' ? 'Voir le Portfolio' : 'View Portfolio'}
                    <ChevronRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
